import $ from 'jquery'
import Swal from 'sweetalert2'
import { editingSaveAction } from '@/services/editingSaveAction'

// One registry for Bootstrap modals, custom dialogs and full-page editors.
const scopes = new Map()
const modalLayers = new WeakMap()
let confirmation = null
const ignored = /pesquis|busca|search|filtro|filter|consulta|visualizarlog/i
const pageModel = /^(form|modal|venda|pedido|orcamento|ordem|osAtual|condicional|entrada|ajuste|movimento|transferencia|devolucao|documento|registro|dados|config|cadastro|perfil|senha|produtoRapido|itemRapido|lancamento)/i
const dialogSelector = '.modal, [role="dialog"], .produto-overlay'
const serialize = value => {
  const seen = new WeakSet()
  return JSON.stringify(value, (key, item) => {
    if (typeof File !== 'undefined' && item instanceof File) return [item.name, item.size, item.lastModified]
    if (item && typeof item === 'object') {
      if (seen.has(item)) return '[reference]'
      seen.add(item)
    }
    return item
  })
}
function isOpen(element) {
  if (!element.isConnected) return false
  if (element.classList.contains('modal')) return element.classList.contains('show')
  return !!element.getClientRects().length && getComputedStyle(element).display !== 'none'
}
function getScope(element, modal = false) {
  if (!scopes.has(element)) scopes.set(element, { element, modal, sources: new Map(), initial: new Map(), touched: false, bypass: false })
  return scopes.get(element)
}
function rebase(scope) {
  scope.initial = new Map([...scope.sources].map(([key, source]) => [key, serialize(source.read())]))
  scope.touched = false
  scope.bypass = false
}
function dirty(scope) {
  return !scope.bypass && scope.touched && isOpen(scope.element) && [...scope.sources].some(([key, source]) => scope.initial.get(key) !== serialize(source.read()))
}
function isPageActive(vm) {
  if (!vm || vm._isDestroyed || !vm.$el?.isConnected) return false
  if (Object.prototype.hasOwnProperty.call(vm.$data, 'modoTela')) return !['lista', 'listagem', 'consulta'].includes(vm.modoTela)
  return true
}
// v-model metadata lets the registry observe actual drafts (arrays/items included),
// without duplicating a form's business fields in a second schema.
function discover(vm) {
  if (!vm.$el?.isConnected || !vm._vnode) return
  for (const scope of scopes.values()) if (!scope.element.isConnected) scopes.delete(scope.element)
  if (!isPageActive(vm)) {
    for (const scope of scopes.values()) if (!scope.modal) {
      for (const [key, source] of scope.sources) if (source.vm === vm) { scope.sources.delete(key); scope.initial.delete(key) }
      if (!scope.sources.size) scopes.delete(scope.element)
    }
  }
  const visit = vnode => {
    if (!vnode) return
    const element = vnode.elm?.nodeType === 1 ? vnode.elm : null
    const expressions = [vnode.data?.model?.expression, ...(vnode.data?.directives || []).filter(directive => directive.name === 'model').map(directive => directive.expression)].filter(Boolean)
    if (element && vnode.context === vm && !element.closest('[data-editing-ignore], .rotina-list-toolbar, .commercial-tree-toolbar')) {
      const modal = element.closest(dialogSelector)
      if (!modal || !ignored.test(modal.id || modal.getAttribute('aria-label') || '')) {
        for (const expression of expressions) {
          const root = expression.match(/^(?:\$data\.)?([\w$]+)/)?.[1]
          const pageEditor = Object.prototype.hasOwnProperty.call(vm.$data, 'modoTela') && isPageActive(vm)
          if (!root || root.startsWith('$') || ignored.test(root) || !(root in vm) || typeof vm[root] === 'function' || (!modal && ((!pageModel.test(root) && !pageEditor) || !isPageActive(vm)))) continue
          const scope = getScope(modal || vm.$el, !!modal), key = `${vm._uid}:${root}`
          if (!scope.sources.has(key)) {
            const read = () => vm[root]
            scope.sources.set(key, { vm, root, read })
            scope.initial.set(key, serialize(read()))
          }
          if (!scope.touched) scope.initial.set(key, serialize(vm[root]))
        }
      }
    }
    for (const child of vnode.children || []) visit(child)
    // Component v-model lives on the component VNode; its own fields are collected
    // by that component's lifecycle hook, preserving the real owner of each draft.
  }
  visit(vm._vnode)
  for (const scope of scopes.values()) if (scope.sources.has('dom') && scope.sources.size > 1) {
    scope.sources.delete('dom'); scope.initial.delete('dom')
  }
  for (const scope of scopes.values()) if (!scope.touched) {
    for (const [key, source] of scope.sources) if (source.vm === vm) scope.initial.set(key, serialize(source.read()))
  }
}
function activeScopes(vm, modalOnly = false) {
  return [...scopes.values()].filter(scope => (!modalOnly || scope.modal) && [...scope.sources.values()].some(source => !vm || source.vm === vm) && (scope.modal || [...scope.sources.values()].some(source => isPageActive(source.vm))))
}
function dialogElement(vm, selector) {
  if (selector instanceof Element) return selector
  const id = selector.replace(/^#/, '')
  if (vm.$el?.id === id || vm.$el?.dataset.relationshipOriginalId === id) return vm.$el
  return vm.$relationshipModal(selector)[0]
}
function acknowledgeDialog(vm, selector) {
  const scope = scopes.get(dialogElement(vm, selector))
  if (scope) rebase(scope)
}
function discardsOnClose(scope) {
  return scope && !scope.element.hasAttribute('data-editing-retained') && dirty(scope)
}
export function confirmDiscard(pending = activeScopes().filter(dirty)) {
  if (confirmation) return confirmation
  const previous = document.activeElement
  const save = editingSaveAction(pending)
  let saveRequested = false
  let confirmed = false
  const keepFocus = event => { if (Swal.getContainer()?.contains(event.target)) event.stopImmediatePropagation() }
  confirmation = Swal.fire({ icon: 'warning', title: 'Cancelar alterações?', text: 'As alterações não salvas serão descartadas. Deseja mesmo sair?', width: '44rem', showCancelButton: true, showDenyButton: true, confirmButtonText: 'Cancelar e sair', denyButtonText: 'Salvar alterações', denyButtonColor: '#198754', cancelButtonText: 'Continuar editando', focusCancel: true, returnFocus: false, keydownListenerCapture: true,
    didOpen() {
      const layers = [...document.querySelectorAll('.modal.show, .relationship-workspace')].map(element => Number(getComputedStyle(element).zIndex) || 0)
      Swal.getContainer().style.zIndex = String(Math.max(1060, ...layers) + 20)
      document.addEventListener('focusin', keepFocus, true)
      const saveButton = Swal.getDenyButton()
      saveButton.disabled = !save?.enabled
      if (saveButton.disabled) saveButton.title = save ? 'Conclua os campos obrigatórios para salvar.' : 'Este editor não possui uma ação de salvamento disponível.'
      Swal.getCancelButton()?.focus()
    },
    preDeny() { saveRequested = true },
    willClose() { document.removeEventListener('focusin', keepFocus, true) },
    didClose() {
      if (saveRequested) save?.run()
      else if (!confirmed && previous?.isConnected) previous.focus()
    }
  }).then(result => { confirmed = result.isConfirmed; return confirmed }).finally(() => { confirmation = null })
  return confirmation
}
const editing = {
  hasChanges(vm) { return activeScopes(vm).some(dirty) },
  savedDocument(vm) { activeScopes(vm).forEach(rebase) },
  // Mark only the saved modal, or the page editor when no modal is supplied.
  saved(vm, selector) {
    if (selector) {
      acknowledgeDialog(vm, selector)
      return
    }
    for (const scope of activeScopes(vm)) if (!scope.modal) rebase(scope)
  },
  // A page can persist independent models (e.g. account data and password).
  // Acknowledge only the models actually saved; keep other drafts protected.
  savedModels(vm, roots) {
    for (const scope of activeScopes(vm)) if (!scope.modal) {
      for (const [key, source] of scope.sources) {
        if (source.vm === vm && roots.includes(source.root)) scope.initial.set(key, serialize(source.read()))
      }
    }
  },
  // Call only after validation and transferring the values to the owning draft.
  // Acknowledges this dialog; the document still needs to be saved separately.
  apply(vm, selector, close, retainedIn) {
    acknowledgeDialog(vm, selector)
    const owner = retainedIn && scopes.get(dialogElement(vm, retainedIn))
    if (owner) owner.touched = true
    return close()
  },
  async leave(vm, action) {
    const pending = activeScopes(vm).filter(dirty)
    if (pending.length && !await confirmDiscard(pending)) return false
    pending.forEach(scope => { scope.bypass = true })
    return action()
  },
  register(vm, element, read) {
    const scope = getScope(element, element.matches(dialogSelector)), key = `${vm._uid}:explicit`
    scope.sources.set(key, { vm, root: 'explicit', read }); rebase(scope)
    return () => { scope.sources.delete(key); scope.initial.delete(key); if (!scope.sources.size) scopes.delete(element) }
  }
}
export default {
  install(Vue, { router }) {
    Vue.prototype.$editing = editing
    Vue.mixin({
      beforeCreate() {
        const methods = this.$options.methods
        if (!methods) return
        this.$options.methods = { ...methods }
        for (const [name, method] of Object.entries(methods)) {
          if (/^(voltarParaListagem|voltarParaLista|voltarListagem|cancelarEdicao|cancelarFormulario|fecharFormulario)$/.test(name)) {
            this.$options.methods[name] = function (...args) { return editing.leave(this, () => method.apply(this, args)) }
          }
        }
      },
      mounted() { this.$nextTick(() => discover(this)) },
      updated() { discover(this) },
      beforeDestroy() {
        for (const [element, scope] of scopes) {
          for (const [key, source] of scope.sources) if (source.vm === this) { scope.sources.delete(key); scope.initial.delete(key) }
          if (!scope.sources.size) scopes.delete(element)
        }
      }
    })
    const touch = event => {
      // Masks and widgets dispatch synthetic input while initializing. Only a real
      // user action starts dirty tracking; subsequent model updates are compared.
      if (!event.isTrusted) return
      const target = event.target
      if (!(target instanceof Element)) return
      const selectOwner = target.closest('.app-select-panel')?._appSelectOwner
      const selectingOption = selectOwner && ((event.type === 'click' && target.closest('[role="option"]')) || (event.type === 'keydown' && event.key === 'Enter'))
      if (target.closest('.swal2-container') || (target.closest('[data-editing-ignore]') && !selectingOption)) return
      if (event.type === 'keydown' && ['Escape', 'Tab'].includes(event.key)) return
      if (event.type === 'click' && target.closest('[role="tab"], [data-bs-toggle="tab"], [data-bs-toggle="pill"], [data-bs-dismiss="modal"], .btn-close')) return
      for (const scope of scopes.values()) if (scope.element.contains(selectOwner || target) && isOpen(scope.element)) scope.touched = true
    }
    document.addEventListener('input', touch, true)
    document.addEventListener('change', touch, true)
    document.addEventListener('click', touch, true)
    document.addEventListener('keydown', touch, true)
    $(document).on('show.bs.modal.editing', '.modal', function () {
      const parents = [...document.querySelectorAll('.modal.show, .relationship-workspace')].filter(element => element !== this)
      if (parents.length) {
        const layer = Math.max(...parents.map(element => Number(getComputedStyle(element).zIndex) || 1055)) + 20
        modalLayers.set(this, { layer, previousZ: this.style.zIndex, backdrops: document.querySelectorAll('.modal-backdrop').length })
        this.style.zIndex = String(layer)
      }
      if (ignored.test(this.id) || this.matches('[data-editing-ignore]')) return
      const scope = getScope(this, true)
      // Fallback for legacy and generated controls without v-model metadata.
      // Actual model roots remain the source for arrays and nonvisible tabs.
      let owner = this
      while (owner && !owner.__vue__) owner = owner.parentElement
      const read = () => [...this.querySelectorAll('input,textarea,select')]
        .filter(input => !['search', 'hidden', 'button', 'submit'].includes(input.type) && !input.closest('[data-editing-ignore]'))
        .map(input => input.type === 'checkbox' || input.type === 'radio' ? input.checked : input.type === 'file' ? [...input.files].map(file => [file.name, file.size, file.lastModified]) : input.value)
      scope.sources.set('dom', { vm: owner?.__vue__, root: 'dom', read })
      rebase(scope)
    })
    $(document).on('shown.bs.modal.editing', '.modal', function () {
      const layer = modalLayers.get(this)
      if (layer) [...document.querySelectorAll('.modal-backdrop')].slice(layer.backdrops).forEach(backdrop => { backdrop.style.zIndex = String(layer.layer - 1) })
    })
    $(document).on('hidden.bs.modal.editing', '.modal', function () {
      const layer = modalLayers.get(this)
      if (layer) { this.style.zIndex = layer.previousZ; modalLayers.delete(this) }
      if (document.querySelector('.modal.show')) document.body.classList.add('modal-open')
    })
    $(document).on('hide.bs.modal.editing', '.modal', function (event) {
      const scope = scopes.get(this)
      if (!discardsOnClose(scope)) return
      event.preventDefault()
      if (scope.asking) return
      scope.asking = true
      confirmDiscard([scope]).then(confirmed => {
        scope.asking = false
        if (confirmed && this.isConnected) { scope.bypass = true; $(this).modal('hide') }
        else this.querySelector('input:not(:disabled), button')?.focus()
      })
    })
    // Capture explicit dismissals before local handlers can reset a draft.
    let replay = null
    document.addEventListener('click', event => {
      const overlay = event.target.matches?.('.produto-overlay') ? event.target : null
      const button = overlay || event.target.closest?.('button, a, [data-bs-dismiss="modal"]')
      if (!button || button === replay || button.closest('.swal2-container')) return
      const label = [button.textContent, button.title, button.getAttribute('aria-label')].filter(Boolean).join(' ').trim()
      if (!overlay && !button.matches('[data-bs-dismiss="modal"], .btn-close, [data-editing-close]') && !/^(cancelar|fechar|voltar)(?:\s|$)/i.test(label)) return
      const modal = button.closest(dialogSelector)
      const pending = (overlay ? activeScopes().filter(scope => overlay.contains(scope.element)) : modal ? [scopes.get(modal)].filter(Boolean) : activeScopes().filter(scope => !scope.modal && scope.element.contains(button))).filter(discardsOnClose)
      if (!pending.length) return
      event.preventDefault(); event.stopImmediatePropagation()
      confirmDiscard(pending).then(confirmed => {
        if (!confirmed || !button.isConnected) return
        pending.forEach(scope => { scope.bypass = true })
        replay = button; button.click(); replay = null
      })
    }, true)
    router.beforeEach(async (to, from, next) => {
      if (to.fullPath === from.fullPath) return next()
      const pending = activeScopes().filter(dirty)
      if (pending.length && !await confirmDiscard(pending)) return next(false)
      pending.forEach(scope => { scope.bypass = true })
      // Allow existing cleanup listeners to run after the user's decision.
      document.querySelectorAll('.modal.show').forEach(element => $(element).modal('hide'))
      next()
    })
    window.addEventListener('beforeunload', event => {
      if (!activeScopes().some(dirty)) return
      event.preventDefault(); event.returnValue = ''
    })
  }
}
